/**
 * AlcoaSummaryCard - ALCOA+ Regulatory Compliance Summary
 * ============================================================================
 * DC-513: ALCOA+ summary card for Journey views
 *
 * Displays:
 * - Overall compliance score with visual gauge
 * - Compliance level badge (exemplary/compliant/partial/non_compliant/critical)
 * - Individual principle scores as mini-bars
 * - Findings summary by severity
 * - Quick actions (run assessment, view details)
 *
 * Design: Clean, professional card optimized for regulatory context
 *
 * @version 1.0.0
 * @created 2025-12-17
 * ============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { RegulatoryBasisDrawer } from '../evidence/RegulatoryBasisDrawer';
import type { EducationalMetric, PrincipleLineage } from '../evidence/RegulatoryBasisDrawer';
import type { StationId } from '../value-drivers/LineageIndicator';
import type { RegulatoryRequirement } from '../../types/evidence';
import { useApiClient } from '../../hooks/useApiClient';
import './alcoa-summary-card.css';

export type AssessmentBasis = 'evidence' | 'indicator' | 'not_assessable';

/**
 * Upstream stations each principle's evidence derives from (the ALCOA+ lens
 * consumption map). Native / indicator principles consume no station.
 */
const PRINCIPLE_SOURCE_STATIONS: Record<string, StationId[]> = {
  accurate: ['data_quality', 'ml_readiness'],
  complete: ['data_quality'],
  consistent: ['data_quality'],
  legible: ['ontology_inference'],
  enduring: ['live_checks'],
  available: ['live_checks'],
  attributable: [],
  original: [],
  contemporaneous: [],
};

// ============================================================================
// Types
// ============================================================================

export type ComplianceLevel = 'exemplary' | 'compliant' | 'partial' | 'non_compliant' | 'critical';

export interface PrincipleScore {
  principle: string;
  score: number;
  label: string;
}

export interface FindingsSummary {
  critical: number;
  major: number;
  minor: number;
  observation: number;
  total: number;
}

export interface AlcoaAssessmentSummary {
  id?: string;
  overallScore: number;
  complianceLevel: ComplianceLevel;
  principleScores: {
    attributable: number;
    legible: number;
    contemporaneous: number;
    original: number;
    accurate: number;
    complete: number;
    consistent: number;
    enduring: number;
    available: number;
  };
  findingsSummary: FindingsSummary;
  createdAt?: string;
  assetId?: string;
  assetName?: string;
  /** Per-principle assessment basis (evidence / indicator / not_assessable). */
  principleBases?: Record<string, AssessmentBasis>;
  /** Per-principle component-score breakdown for the lineage drawer. */
  componentScores?: Record<string, Record<string, number>>;
  /** True when no principle could be scored from evidence. */
  insufficientEvidence?: boolean;
  /** Provenance: synthesis lineage event id for this assessment. */
  lineageEventId?: string;
  /** Assessor version (provenance record). */
  assessorVersion?: string;
}

export interface AlcoaSummaryCardProps {
  /** Journey ID for API calls */
  journeyId: string;
  /** Asset ID for specific asset assessment */
  assetId?: string;
  /** Pre-loaded assessment data (optional) */
  assessment?: AlcoaAssessmentSummary;
  /** Callback when "Run Assessment" is clicked */
  onRunAssessment?: () => void;
  /** Callback when "View Details" is clicked */
  onViewDetails?: (assessmentId: string) => void;
  /** Callback when findings badge is clicked */
  onViewFindings?: (severity?: string) => void;
  /** Show compact mode for sidebar/small spaces */
  compact?: boolean;
  /** Show loading state */
  loading?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

// Cream/teal tokens (v2.0 beat 8). Exemplary and compliant deliberately share
// --tri-good — the lime/green split has no cream-scale equivalent; the badge
// label text carries the level.
const COMPLIANCE_LEVELS: Record<ComplianceLevel, { label: string; color: string; description: string }> = {
  exemplary: {
    label: 'EXEMPLARY',
    color: 'var(--tri-good)',
    description: 'Exceeds regulatory requirements',
  },
  compliant: {
    label: 'COMPLIANT',
    color: 'var(--tri-good)',
    description: 'Meets regulatory requirements',
  },
  partial: {
    label: 'PARTIAL',
    color: 'var(--tri-soft)',
    description: 'Significant gaps identified',
  },
  non_compliant: {
    label: 'NON-COMPLIANT',
    color: 'var(--tri-terra)',
    description: 'Major issues require remediation',
  },
  critical: {
    label: 'CRITICAL',
    color: 'var(--tri-hard)',
    description: 'Not suitable for regulatory submission',
  },
};

const PRINCIPLE_LABELS: Record<string, string> = {
  attributable: 'Attributable',
  legible: 'Legible',
  contemporaneous: 'Contemporaneous',
  original: 'Original',
  accurate: 'Accurate',
  complete: 'Complete',
  consistent: 'Consistent',
  enduring: 'Enduring',
  available: 'Available',
};

const PRINCIPLE_DESCRIPTIONS: Record<string, string> = {
  attributable: 'All data entries must be traceable to the person who created, modified, or deleted them.',
  legible: 'Data must be readable, permanent, and understandable throughout its required retention period.',
  contemporaneous: 'Data must be recorded at the time the activity is performed, not retrospectively.',
  original: 'The first capture of data must be preserved, or a certified true copy must be available.',
  accurate: 'Data must be correct, truthful, and free from errors.',
  complete: 'All data must be present, including any repeat or reanalysis data, with no selective reporting.',
  consistent: 'Data should be internally consistent with no unexplained contradictions across records.',
  enduring: 'Data must be durable and maintained throughout the required retention period.',
  available: 'Data must be accessible to authorized users when needed throughout its lifecycle.',
};

const PRINCIPLE_ORDER = [
  'attributable',
  'legible',
  'contemporaneous',
  'original',
  'accurate',
  'complete',
  'consistent',
  'enduring',
  'available',
];

// ============================================================================
// Helper Functions
// ============================================================================

function getScoreColor(score: number): string {
  if (score >= 70) return 'var(--tri-good)'; // ≥90 and 70–89 deliberately collapse
  if (score >= 50) return 'var(--tri-soft)';
  if (score >= 25) return 'var(--tri-terra)';
  return 'var(--tri-hard)';
}

function formatDate(dateString?: string): string {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ============================================================================
// Sub-Components
// ============================================================================

interface ScoreGaugeProps {
  score: number;
  size?: 'small' | 'medium' | 'large';
}

const ScoreGauge: React.FC<ScoreGaugeProps> = ({ score, size = 'medium' }) => {
  const sizes = {
    small: { width: 60, strokeWidth: 4, fontSize: 14 },
    medium: { width: 100, strokeWidth: 6, fontSize: 24 },
    large: { width: 140, strokeWidth: 8, fontSize: 32 },
  };

  const { width, strokeWidth, fontSize } = sizes[size];
  const radius = (width - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = (score / 100) * circumference;
  const color = getScoreColor(score);

  return (
    <div className="alcoa-score-gauge" style={{ width, height: width }}>
      <svg width={width} height={width} viewBox={`0 0 ${width} ${width}`}>
        {/* Background circle */}
        <circle
          cx={width / 2}
          cy={width / 2}
          r={radius}
          fill="none"
          stroke="var(--tri-track)"
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={width / 2}
          cy={width / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={`${progress} ${circumference}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${width / 2} ${width / 2})`}
          style={{ transition: 'stroke-dasharray 0.5s ease' }}
        />
      </svg>
      <div className="alcoa-score-value" style={{ fontSize }}>
        {Math.round(score)}
      </div>
    </div>
  );
};

interface PrincipleBarProps {
  principle: string;
  score: number;
  compact?: boolean;
  onViewBasis?: (principle: string) => void;
}

const PrincipleBar: React.FC<PrincipleBarProps> = ({ principle, score, compact, onViewBasis }) => {
  const color = getScoreColor(score);
  const label = PRINCIPLE_LABELS[principle] || principle;

  return (
    <div className={`alcoa-principle-bar ${compact ? 'compact' : ''}`} title={`${label}: ${score}%`}>
      <span className="principle-label">{compact ? label.charAt(0) : label}</span>
      <div className="principle-bar-track">
        <div
          className="principle-bar-fill"
          style={{
            width: `${score}%`,
            backgroundColor: color,
          }}
        />
      </div>
      {!compact && <span className="principle-score">{score}</span>}
      {!compact && onViewBasis && (
        <button
          className="alcoa-basis-btn"
          onClick={(e) => {
            e.stopPropagation();
            onViewBasis(principle);
          }}
          title={`View regulatory basis for ${label}`}
          aria-label={`View regulatory basis for ${label}`}
        >
          ?
        </button>
      )}
    </div>
  );
};

interface FindingsBadgeProps {
  severity: 'critical' | 'major' | 'minor' | 'observation';
  count: number;
  onClick?: () => void;
}

const FindingsBadge: React.FC<FindingsBadgeProps> = ({ severity, count, onClick }) => {
  if (count === 0) return null;

  const colors: Record<string, { bg: string; text: string }> = {
    critical: { bg: 'var(--tri-hard-bg)', text: 'var(--tri-hard)' },
    major: { bg: 'rgba(var(--tri-terra-rgb), 0.12)', text: 'var(--tri-terra)' },
    minor: { bg: 'var(--tri-soft-bg)', text: 'var(--tri-soft)' },
    observation: { bg: 'var(--tri-neutral-bg)', text: 'var(--tri-neutral)' },
  };

  const { bg, text } = colors[severity];

  return (
    <button
      className="alcoa-finding-badge"
      style={{ backgroundColor: bg, color: text }}
      onClick={onClick}
      title={`${count} ${severity} finding${count > 1 ? 's' : ''}`}
    >
      <span className="badge-count">{count}</span>
      <span className="badge-label">{severity}</span>
    </button>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const AlcoaSummaryCard: React.FC<AlcoaSummaryCardProps> = ({
  journeyId,
  assetId,
  assessment: initialAssessment,
  onRunAssessment,
  onViewDetails,
  onViewFindings,
  compact = false,
  loading: externalLoading = false,
}) => {
  const { authFetch } = useApiClient();
  const [assessment, setAssessment] = useState<AlcoaAssessmentSummary | null>(initialAssessment || null);
  const [loading, setLoading] = useState(!initialAssessment);
  const [error, setError] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [journeyAssetId, setJourneyAssetId] = useState<string | null>(null);

  // Regulatory Basis Drawer state (DC-1064)
  const [basisDrawerOpen, setBasisDrawerOpen] = useState(false);
  const [basisMetric, setBasisMetric] = useState<EducationalMetric | null>(null);
  const [basisRequirement, setBasisRequirement] = useState<RegulatoryRequirement | null>(null);
  const [basisLineage, setBasisLineage] = useState<PrincipleLineage | null>(null);
  const [basisLoading, setBasisLoading] = useState(false);
  const [basisError, setBasisError] = useState<string | null>(null);

  // Fetch journey assets if no assetId provided (for "Run Assessment" button)
  useEffect(() => {
    if (!assetId && journeyId) {
      fetchJourneyAssets();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchJourneyAssets is defined in component
  }, [journeyId, assetId]);

  // Fetch assessment if not provided
  useEffect(() => {
    if (!initialAssessment && journeyId) {
      fetchAssessment();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchAssessment is defined in component
  }, [journeyId, assetId, initialAssessment]);

  const fetchJourneyAssets = async () => {
    try {
      // FIX (2025-12-30): Use correct endpoint - journey data includes assets
      // API returns { journey: { ... } } wrapper
      const response = await authFetch(`/api/journey/${journeyId}`);
      if (response.ok) {
        const data = await response.json();
        // Handle both wrapped { journey: {...} } and unwrapped responses
        const journey = data.journey || data;
        const assets = journey.assets || [];
        if (assets.length > 0) {
          // Use the first dataset asset for ALCOA+ assessment
          const datasetAsset = assets.find((a: { type: string }) => a.type === 'dataset') || assets[0];
          setJourneyAssetId(datasetAsset.asset_id || datasetAsset.id);
        }
      }
    } catch (err) {
      console.warn('[AlcoaSummaryCard] Failed to fetch journey assets:', err);
    }
  };

  const fetchAssessment = async () => {
    try {
      setLoading(true);
      setError(null);

      const url = assetId
        ? `/api/v1/journeys/${journeyId}/assets/${assetId}/alcoa`
        : `/api/v1/journeys/${journeyId}/alcoa/summary`;

      const response = await fetch(url);

      if (response.status === 404) {
        // No assessment yet
        setAssessment(null);
        setLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch assessment: ${response.statusText}`);
      }

      const data = await response.json();

      // FIX (2025-12-30): Handle different response structures
      // Asset assessment returns: { result: { overallScore, complianceLevel, ... } }
      // Summary endpoint returns: { summary: { averageScore, complianceLevelDistribution, ... } }
      let assessmentData = data.result;

      if (!assessmentData && data.summary) {
        // Transform summary format to assessment format
        const summary = data.summary;

        // Derive compliance level from distribution (pick the one with count > 0, prioritize worst)
        let derivedLevel: ComplianceLevel = 'partial';
        const dist = summary.complianceLevelDistribution || {};
        if (dist.critical > 0) derivedLevel = 'critical';
        else if (dist.non_compliant > 0) derivedLevel = 'non_compliant';
        else if (dist.partial > 0) derivedLevel = 'partial';
        else if (dist.compliant > 0) derivedLevel = 'compliant';
        else if (dist.exemplary > 0) derivedLevel = 'exemplary';

        assessmentData = {
          id: summary.journeyId,
          overallScore: summary.averageScore || 0,
          complianceLevel: derivedLevel,
          principleScores: summary.principleAverages || {
            attributable: 0, legible: 0, contemporaneous: 0, original: 0,
            accurate: 0, complete: 0, consistent: 0, enduring: 0, available: 0,
          },
          findingsSummary: summary.totalFindings || {
            critical: 0, major: 0, minor: 0, observation: 0, total: 0,
          },
          createdAt: summary.lastAssessmentAt,
        };
      }

      setAssessment(assessmentData);
      setLoading(false);
    } catch (err: unknown) {
      console.error('[AlcoaSummaryCard] Failed to fetch assessment:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch assessment');
      setLoading(false);
    }
  };

  const handleRunAssessment = async () => {
    if (onRunAssessment) {
      onRunAssessment();
      return;
    }

    // Use provided assetId or the journey's first asset
    const targetAssetId = assetId || journeyAssetId;
    if (!targetAssetId) {
      console.warn('[AlcoaSummaryCard] Cannot run assessment: no assets available in this journey');
      setError('No assets available for assessment. Please upload a dataset first.');
      return;
    }

    try {
      setIsRunning(true);
      setError(null);

      const response = await fetch(`/api/v1/journeys/${journeyId}/assets/${targetAssetId}/alcoa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error(`Assessment failed: ${response.statusText}`);
      }

      const data = await response.json();
      setAssessment(data.result);
      setIsRunning(false);
    } catch (err: unknown) {
      console.error('[AlcoaSummaryCard] Assessment failed:', err);
      setError(err instanceof Error ? err.message : 'Assessment failed');
      setIsRunning(false);
    }
  };

  // DC-1064: Handler to view regulatory basis for a principle
  const handleViewBasis = useCallback(async (principle: string) => {
    if (!assessment) return;

    // Map principle key to proper name
    const principleLabel = PRINCIPLE_LABELS[principle] || principle;
    const principleScore = assessment.principleScores[principle as keyof typeof assessment.principleScores];

    // Assessment basis drives status + the lineage drawer's framing.
    const basis: AssessmentBasis = assessment.principleBases?.[principle] ?? 'evidence';

    // Determine status based on basis + score and compliance thresholds.
    let status: EducationalMetric['status'] = 'not_assessed';
    if (basis === 'not_assessable') status = 'not_assessed';
    else if (principleScore >= 90) status = 'compliant';
    else if (principleScore >= 50) status = 'partial';
    else status = 'non_compliant';

    // Build the full-chain lineage context for the drawer.
    const lineage: PrincipleLineage = {
      basis,
      componentScores: assessment.componentScores?.[principle],
      // Only evidence-basis principles actually consumed an upstream station.
      sourceStations: basis === 'evidence' ? (PRINCIPLE_SOURCE_STATIONS[principle] ?? []) : [],
      provenance: {
        eventId: assessment.lineageEventId,
        assessedAt: assessment.createdAt ? formatDate(assessment.createdAt) : undefined,
        assessorVersion: assessment.assessorVersion,
      },
    };

    // Show drawer immediately in loading state
    setBasisDrawerOpen(true);
    setBasisLoading(true);
    setBasisError(null);
    setBasisLineage(lineage);
    setBasisMetric({
      name: principleLabel,
      score: principleScore,
      status,
      description: PRINCIPLE_DESCRIPTIONS[principle] || `${principleLabel} data integrity principle.`,
    });

    try {
      // Fetch requirement and findings in parallel
      const requirementId = principle;
      const [reqResponse, findingsResponse] = await Promise.all([
        fetch(`/api/v1/regulatory/library/requirements/${encodeURIComponent(requirementId)}`),
        fetch(`/api/v1/journeys/${journeyId}/alcoa/findings?principle=${principle}&prioritized=true`),
      ]);

      // Parse requirement
      if (!reqResponse.ok) {
        throw new Error(`Failed to fetch requirement: ${reqResponse.status}`);
      }
      const reqData = await reqResponse.json();
      setBasisRequirement(reqData.requirement || null);

      // Parse findings and extract recommendations
      let findings: string[] = [];
      let recommendations: string[] = [];

      if (findingsResponse.ok) {
        const findingsData = await findingsResponse.json();
        const principleFindings = findingsData.findings || [];

        findings = principleFindings.map((f: { severity: string; title: string; description: string }) =>
          `[${f.severity.toUpperCase()}] ${f.title}: ${f.description}`
        );

        recommendations = principleFindings
          .filter((f: { remediation?: string }) => f.remediation)
          .map((f: { remediation: string }) => f.remediation);
      }

      // Update metric with findings and recommendations
      setBasisMetric({
        name: principleLabel,
        score: principleScore,
        status,
        description: PRINCIPLE_DESCRIPTIONS[principle] || `${principleLabel} data integrity principle.`,
        findings,
        recommendations,
      });
    } catch (err) {
      console.error('[AlcoaSummaryCard] Failed to fetch regulatory basis:', err);
      setBasisError(err instanceof Error ? err.message : 'Failed to load regulatory basis');
    } finally {
      setBasisLoading(false);
    }
  }, [assessment, journeyId]);

  const handleCloseBasisDrawer = useCallback(() => {
    setBasisDrawerOpen(false);
    setBasisMetric(null);
    setBasisRequirement(null);
    setBasisLineage(null);
    setBasisError(null);
  }, []);

  // Loading state
  if (loading || externalLoading) {
    return (
      <div className={`alcoa-summary-card ${compact ? 'compact' : ''} loading`}>
        <div className="alcoa-card-header">
          <div className="alcoa-card-icon">
            <span role="img" aria-label="Regulatory">&#x2696;</span>
          </div>
          <h3 className="alcoa-card-title">ALCOA+ Compliance</h3>
        </div>
        <div className="alcoa-loading-skeleton">
          <div className="skeleton-gauge" />
          <div className="skeleton-bars">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="skeleton-bar" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // No assessment yet
  if (!assessment) {
    return (
      <div className={`alcoa-summary-card ${compact ? 'compact' : ''} empty`}>
        <div className="alcoa-card-header">
          <div className="alcoa-card-icon">
            <span role="img" aria-label="Regulatory">&#x2696;</span>
          </div>
          <h3 className="alcoa-card-title">ALCOA+ Compliance</h3>
        </div>
        <div className="alcoa-empty-state">
          <p>No regulatory assessment available</p>
          {(assetId || journeyAssetId) && (
            <button
              className="alcoa-run-btn primary"
              onClick={handleRunAssessment}
              disabled={isRunning}
            >
              {isRunning ? 'Running...' : 'Run ALCOA+ Assessment'}
            </button>
          )}
          {!assetId && !journeyAssetId && (
            <p className="alcoa-no-assets-hint">Upload a dataset to enable ALCOA+ assessment</p>
          )}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`alcoa-summary-card ${compact ? 'compact' : ''} error`}>
        <div className="alcoa-card-header">
          <div className="alcoa-card-icon">
            <span role="img" aria-label="Regulatory">&#x2696;</span>
          </div>
          <h3 className="alcoa-card-title">ALCOA+ Compliance</h3>
        </div>
        <div className="alcoa-error-state">
          <p>Failed to load assessment</p>
          <button className="alcoa-retry-btn" onClick={fetchAssessment}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // FIX (2025-12-30): Add defensive fallback for unknown compliance levels
  const complianceInfo = COMPLIANCE_LEVELS[assessment.complianceLevel] || COMPLIANCE_LEVELS.partial;
  const findingsSummary = assessment.findingsSummary || { critical: 0, major: 0, minor: 0, observation: 0, total: 0 };

  // Evidence-backed-headline-vs-indicators split. Older assessments (pre-basis)
  // have no principleBases — fall back to the legacy single list in that case.
  const principleBases = assessment.principleBases;
  const hasBases = !!principleBases && Object.keys(principleBases).length > 0;
  const evidencePrinciples = hasBases
    ? PRINCIPLE_ORDER.filter((p) => principleBases![p] === 'evidence')
    : PRINCIPLE_ORDER;
  const indicatorPrinciples = hasBases
    ? PRINCIPLE_ORDER.filter((p) => principleBases![p] !== 'evidence')
    : [];

  const renderPrincipleBar = (principle: string) => (
    <PrincipleBar
      key={principle}
      principle={principle}
      score={assessment.principleScores[principle as keyof typeof assessment.principleScores]}
      compact={compact}
      onViewBasis={!compact ? handleViewBasis : undefined}
    />
  );

  return (
    <div className={`alcoa-summary-card ${compact ? 'compact' : ''} ${assessment.complianceLevel}`}>
      {/* Header */}
      <div className="alcoa-card-header">
        <div className="alcoa-card-icon">
          <span role="img" aria-label="Regulatory">&#x2696;</span>
        </div>
        <div className="alcoa-card-title-group">
          <h3 className="alcoa-card-title">ALCOA+ Compliance</h3>
          {assessment.assetName && (
            <span className="alcoa-asset-name">{assessment.assetName}</span>
          )}
        </div>
        <div
          className="alcoa-compliance-badge"
          style={{ backgroundColor: complianceInfo.color }}
          title={complianceInfo.description}
        >
          {complianceInfo.label}
        </div>
      </div>

      {/* Main Content */}
      <div className="alcoa-card-content">
        {/* Score Gauge */}
        <div className="alcoa-score-section">
          <ScoreGauge score={assessment.overallScore} size={compact ? 'small' : 'medium'} />
          {!compact && assessment.insufficientEvidence && (
            <p className="alcoa-insufficient-badge" title="No principle could be scored from evidence">
              Insufficient evidence — run Data Quality / FAIR / Ontology stations
            </p>
          )}
          {!compact && !assessment.insufficientEvidence && (
            <p className="alcoa-compliance-description">
              {complianceInfo.description}
              {hasBases && (
                <span className="alcoa-evidence-note">
                  {' '}Headline from {evidencePrinciples.length} evidence-backed principle{evidencePrinciples.length !== 1 ? 's' : ''}.
                </span>
              )}
            </p>
          )}
        </div>

        {/* Principle Scores */}
        <div className="alcoa-principles-section">
          {!compact && hasBases ? (
            <>
              <h4 className="section-label">Evidence-backed (scored)</h4>
              <div className="alcoa-principles-grid">
                {evidencePrinciples.length > 0
                  ? evidencePrinciples.map(renderPrincipleBar)
                  : <p className="alcoa-group-empty">No principles could be scored from evidence yet.</p>}
              </div>
              {indicatorPrinciples.length > 0 && (
                <>
                  <h4 className="section-label section-label--indicator">
                    Indicators — require source-system evidence
                  </h4>
                  <p className="alcoa-group-note">
                    Dataset-level signals, excluded from the headline score. Confirming these
                    requires source-system evidence (audit trails, e-signatures, chain of custody).
                  </p>
                  <div className="alcoa-principles-grid">
                    {indicatorPrinciples.map(renderPrincipleBar)}
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              {!compact && <h4 className="section-label">Principle Scores</h4>}
              <div className={`alcoa-principles-grid ${compact ? 'compact' : ''}`}>
                {PRINCIPLE_ORDER.map(renderPrincipleBar)}
              </div>
            </>
          )}
        </div>

        {/* Findings Summary */}
        {!compact && findingsSummary.total > 0 && (
          <div className="alcoa-findings-section">
            <h4 className="section-label">Findings ({findingsSummary.total})</h4>
            <div className="alcoa-findings-badges">
              <FindingsBadge
                severity="critical"
                count={findingsSummary.critical}
                onClick={() => onViewFindings?.('critical')}
              />
              <FindingsBadge
                severity="major"
                count={findingsSummary.major}
                onClick={() => onViewFindings?.('major')}
              />
              <FindingsBadge
                severity="minor"
                count={findingsSummary.minor}
                onClick={() => onViewFindings?.('minor')}
              />
              <FindingsBadge
                severity="observation"
                count={findingsSummary.observation}
                onClick={() => onViewFindings?.('observation')}
              />
            </div>
          </div>
        )}

        {/* Compact findings indicator */}
        {compact && findingsSummary.total > 0 && (
          <div className="alcoa-findings-compact">
            {findingsSummary.critical > 0 && (
              <span className="finding-dot critical" title={`${findingsSummary.critical} critical`} />
            )}
            {findingsSummary.major > 0 && (
              <span className="finding-dot major" title={`${findingsSummary.major} major`} />
            )}
            <span className="finding-count">{findingsSummary.total} findings</span>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      {!compact && (
        <div className="alcoa-card-footer">
          {assessment.createdAt && (
            <span className="alcoa-timestamp">
              Last assessed: {formatDate(assessment.createdAt)}
            </span>
          )}
          <div className="alcoa-card-actions">
            {assetId && (
              <button
                className="alcoa-action-btn secondary"
                onClick={handleRunAssessment}
                disabled={isRunning}
              >
                {isRunning ? 'Running...' : 'Re-assess'}
              </button>
            )}
            {assessment.id && onViewDetails && (
              <button
                className="alcoa-action-btn primary"
                onClick={() => onViewDetails(assessment.id!)}
              >
                View Details
              </button>
            )}
          </div>
        </div>
      )}

      {/* DC-1064: Regulatory Basis Drawer */}
      <RegulatoryBasisDrawer
        isOpen={basisDrawerOpen}
        onClose={handleCloseBasisDrawer}
        metric={basisMetric}
        requirement={basisRequirement}
        loading={basisLoading}
        error={basisError}
        lineage={basisLineage}
      />
    </div>
  );
};

export default AlcoaSummaryCard;
